import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useMemo, useState } from 'react';
import { SectionTitle } from '../../../shared/components';
import { Table } from '../../../shared/components/data-display';
import type { Payslip } from '../../hr.api';
import { MONTHS, money, monthLabel } from '../../payroll/payroll.utils';

const ALL = 'all';

const columns = [
  {
    id: 'period',
    dataKey: 'month',
    label: 'Month',
    align: 'left' as const,
    sortable: true,
    renderCell: (_value: unknown, row: Payslip) => monthLabel(row.year, row.month),
  },
  {
    id: 'payable_hours',
    dataKey: 'payable_hours',
    label: 'Payable hrs',
    align: 'center' as const,
    renderCell: (value: unknown) => Number(value ?? 0).toFixed(1),
  },
  { id: 'gross_salary', dataKey: 'gross_salary', label: 'Gross', align: 'right' as const, renderCell: money },
  { id: 'incentive', dataKey: 'incentive', label: 'Incentive', align: 'right' as const, renderCell: money },
  { id: 'advance_deducted', dataKey: 'advance_deducted', label: 'Advance', align: 'right' as const, renderCell: money },
  { id: 'professional_tax', dataKey: 'professional_tax', label: 'PT', align: 'right' as const, renderCell: money },
  { id: 'tds', dataKey: 'tds', label: 'TDS', align: 'right' as const, renderCell: money },
  {
    id: 'net_payable',
    dataKey: 'net_payable',
    label: 'Net paid',
    align: 'right' as const,
    sortable: true,
    renderCell: (value: unknown) => <strong>{money(value)}</strong>,
  },
  {
    id: 'status',
    dataKey: 'status',
    label: 'Status',
    align: 'center' as const,
    renderCell: (value: unknown) => (
      <Chip
        size='small'
        label={String(value ?? 'Unpaid')}
        color={String(value).toUpperCase() === 'PAID' ? 'success' : 'default'}
      />
    ),
  },
];

/**
 * The employee's payslip history.
 *
 * This was a stub — two disabled pickers and an empty render block — because no payslip table
 * existed and `GET /api/hr/employee/{id}` returned an empty list unconditionally. Both now
 * exist, so the pickers filter real rows.
 */
export default function EmployeePaySlipsSection({ payslips = [] }: { payslips?: Payslip[] }) {
  const [month, setMonth] = useState<string>(ALL);
  const [year, setYear] = useState<string>(ALL);

  const years = useMemo(
    () => Array.from(new Set(payslips.map((slip) => slip.year))).sort((a, b) => b - a),
    [payslips]
  );

  const visible = useMemo(
    () =>
      payslips.filter(
        (slip) =>
          (month === ALL || slip.month === Number(month)) &&
          (year === ALL || slip.year === Number(year))
      ),
    [payslips, month, year]
  );

  return (
    <div className='column gap-3'>
      <Divider />

      <SectionTitle
        title='Payslips'
        action={
          <Stack direction='row' gap={2}>
            <TextField
              select
              size='small'
              label='Month'
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              sx={{ minWidth: 140 }}
              disabled={payslips.length === 0}
            >
              <MenuItem value={ALL}>All months</MenuItem>
              {MONTHS.map((name, index) => (
                <MenuItem key={name} value={String(index + 1)}>
                  {name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size='small'
              label='Year'
              value={year}
              onChange={(e) => setYear(e.target.value)}
              sx={{ minWidth: 110 }}
              disabled={payslips.length === 0}
            >
              <MenuItem value={ALL}>All years</MenuItem>
              {years.map((value) => (
                <MenuItem key={value} value={String(value)}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        }
      />

      {payslips.length === 0 ? (
        <div className='column text--secondary'>
          <span>No payslips available</span>
          <span className='text--small'>
            Payslips are written when HR releases the month on the Salary Board.
          </span>
        </div>
      ) : (
        <Table columns={columns} data={visible} emptyMessage='No payslips for that period' />
      )}
    </div>
  );
}
